import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Subscription, Observable } from 'rxjs'
import { finalize } from 'rxjs/operators'
import { AccountService } from '../../services/account.service'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService, RoleItem, AccessLevelInfo } from 'src/app/services/role-loading.service'
import { FormValidationService } from 'src/app/services/form-validation.service'
import { AuthenticatedSubjectDto } from 'src/app/services/dtos'
import { handleArpioError } from 'src/utils/handle-error'

// Re-export interfaces for components that inherit from this base class
export { RoleItem, AccessLevelInfo }

export interface UserItem {
  id: string
  subjectId?: string // Add optional subjectId field
  name: string
  email: string
  selected: boolean
}

export abstract class BaseGroupModal {
  // Common properties for group operations
  selectedRoles: RoleItem[] = []
  selectedUsers: UserItem[] = []
  availableRoles: RoleItem[] = []
  availableUsers: UserItem[] = []

  // Form and loading states
  groupForm: FormGroup
  loadingRoles = false
  saving = false
  saveSubscription: Subscription = null

  // Authenticated user tracking
  authenticatedSubject: AuthenticatedSubjectDto | null = null

  constructor(
    protected formBuilder: FormBuilder,
    protected accountService: AccountService,
    protected apiService: ApiService,
    protected uiService: UiService,
    protected accessLevelService: AccessLevelService,
    protected roleLoadingService: RoleLoadingService,
    protected formValidationService: FormValidationService
  ) {
    this.initializeForm()
  }

  // Helper method to identify default account roles
  isDefaultAccountRole(role: RoleItem): boolean {
    return this.accessLevelService.isDefaultAccountRole(role)
  }

  // Helper method to identify the current user
  isCurrentUser(user: UserItem): boolean {
    if (!this.authenticatedSubject || (!user?.id && !user?.subjectId)) {
      return false
    }

    // Try matching both subjectId and id fields
    const isMatchBySubjectId = user.subjectId === this.authenticatedSubject.subjectId
    const isMatchById = user.id === this.authenticatedSubject.subjectId

    return isMatchBySubjectId || isMatchById
  }

  // Abstract methods that must be implemented by child components
  abstract getSaveButtonText(): string
  abstract performSave(groupData: any): Observable<any>
  abstract onSaveSuccess(result: any): void

  // Common form initialization
  protected initializeForm() {
    this.groupForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: [''],
      externalId: ['', this.formValidationService.getExternalIdValidators()],
    })
  }

  // Common roles and users loading
  protected async loadRolesAndUsers() {
    this.loadingRoles = true
    try {
      await Promise.all([this.loadAvailableRoles(), this.loadAvailableUsers(), this.loadAuthenticatedSubject()])
    } catch (error) {
      console.error('Failed to load roles and users:', error)
    } finally {
      this.loadingRoles = false
    }
  }

  private async loadAvailableRoles(): Promise<void> {
    this.availableRoles = await this.roleLoadingService.loadRoles()
  }

  private loadAvailableUsers(): Promise<void> {
    return new Promise((resolve) => {
      this.apiService.listAccountSubjects(this.accountService.accountId).subscribe(
        (userDtos) => {
          this.availableUsers = userDtos
            .filter((userDto) => userDto && (userDto.accountSubjectId || userDto.email)) // Filter out invalid entries
            .map((userDto) => ({
              id: userDto.accountSubjectId || '',
              subjectId: userDto.subjectId || '', // Add subjectId field for current user matching
              name: userDto.name || userDto.email || '',
              email: userDto.email || '',
              selected: false,
            }))

          // Sort users: current user first, then others alphabetically
          this.availableUsers.sort((a, b) => {
            const aIsCurrent = this.isCurrentUser(a)
            const bIsCurrent = this.isCurrentUser(b)

            if (aIsCurrent && !bIsCurrent) return -1
            if (!aIsCurrent && bIsCurrent) return 1
            return a.name.localeCompare(b.name)
          })
          resolve()
        },
        (error) => {
          console.error('💥 Failed to load users for group modal:', error)
          console.error('Error details:', {
            message: error.message,
            status: error.status,
            url: error.url,
          })
          this.availableUsers = []
          resolve()
        }
      )
    })
  }

  private loadAuthenticatedSubject(): Promise<void> {
    return new Promise((resolve) => {
      this.apiService.getAuthenticatedSubject().subscribe(
        (authenticatedSubject) => {
          this.authenticatedSubject = authenticatedSubject
          resolve()
        },
        (error) => {
          console.error('Failed to load authenticated subject:', error)
          this.authenticatedSubject = null
          resolve()
        }
      )
    })
  }


  // Common validation logic
  get isFormValid(): boolean {
    const hasValidForm = this.groupForm.valid

    return hasValidForm
  }

  // Common save logic
  onSave() {
    if (!this.isFormValid) return

    this.saving = true

    const groupData = this.getGroupData()

    this.saveSubscription = this.performSave(groupData)
      .pipe(
        finalize(() => {
          this.saving = false
          this.saveSubscription = null
        })
      )
      .subscribe(
        (result) => {
          this.onSaveSuccess(result)
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  // Common group data preparation
  protected getGroupData() {
    const formData = this.groupForm.value
    return {
      name: formData.name,
      externalId: formData.externalId || null, // Include externalId from form
      roles: this.selectedRoles.map((role) => role.id), // Map to role IDs
      accountSubjects: this.selectedUsers.map((user) => user.id), // Map to account subject IDs
    }
  }

  // Common reset functionality
  protected resetSelections() {
    this.selectedRoles = []
    this.selectedUsers = []
    this.availableRoles.forEach((role) => (role.selected = false))
    this.availableUsers.forEach((user) => (user.selected = false))
  }

  // Common cleanup
  protected cleanup() {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe()
      this.saveSubscription = null
    }
  }

  // Helper methods for role/user selection
  getSelectedRoleIds(): string[] {
    return this.selectedRoles.map((role) => role.id)
  }

  getSelectedUserIds(): string[] {
    return this.selectedUsers.map((user) => user.id)
  }

  // Common form error handling
  get nameErrors() {
    const control = this.groupForm.get('name')
    if (control?.errors && control?.touched) {
      if (control.errors['required']) {
        return 'Group name is required'
      }
    }
    return null
  }

  // Access level display helper
  getAccessLevelDisplay(accessLevel: string): string {
    return this.accessLevelService.getAccessLevelDisplayName(accessLevel as any)
  }

  // External ID validation
  get externalIdErrors() {
    return this.formValidationService.getExternalIdErrorsFromForm(this.groupForm, 'externalId')
  }

  // Custom checkbox methods for roles
  areAllRolesSelected(): boolean {
    return this.selectedRoles.length > 0 && this.selectedRoles.length === this.availableRoles.length
  }

  areSomeRolesSelected(): boolean {
    return this.selectedRoles.length > 0 && this.selectedRoles.length < this.availableRoles.length
  }

  isRoleSelected(role: RoleItem): boolean {
    return this.selectedRoles.some((r) => r.id === role.id)
  }

  toggleAllRoles(event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      this.selectedRoles = [...this.availableRoles]
    } else {
      this.selectedRoles = []
    }
  }

  toggleRole(role: RoleItem, event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      if (!this.isRoleSelected(role)) {
        this.selectedRoles = [...this.selectedRoles, role]
      }
    } else {
      this.selectedRoles = this.selectedRoles.filter((r) => r.id !== role.id)
    }
  }

  // Custom checkbox methods for users
  areAllUsersSelected(): boolean {
    return this.selectedUsers.length > 0 && this.selectedUsers.length === this.availableUsers.length
  }

  areSomeUsersSelected(): boolean {
    return this.selectedUsers.length > 0 && this.selectedUsers.length < this.availableUsers.length
  }

  isUserSelected(user: UserItem): boolean {
    return this.selectedUsers.some((u) => u.id === user.id)
  }

  toggleAllUsers(event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      this.selectedUsers = [...this.availableUsers]
    } else {
      this.selectedUsers = []
    }
  }

  toggleUser(user: UserItem, event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      if (!this.isUserSelected(user)) {
        this.selectedUsers = [...this.selectedUsers, user]
      }
    } else {
      this.selectedUsers = this.selectedUsers.filter((u) => u.id !== user.id)
    }
  }

  // Generate tooltip content for access level badges
  getAccessLevelTooltip(accessLevelInfo: AccessLevelInfo): string {
    if (!accessLevelInfo.applications || accessLevelInfo.applications.length === 0) {
      return 'All applications'
    }

    const badgeHtml = accessLevelInfo.applications.map((name) => `<span class="tooltip-badge">${name}</span>`).join('')
    return `<div class="tooltip-badges-container">${badgeHtml}</div>`
  }
}
